import React, { useCallback, useEffect, useState } from 'react';
import * as adminApi from '../api/adminApi';
import { PAGE_SIZE, formatDate } from '../lib/format';
import { normalizeListResponse, pickErrorMessage } from '../lib/apiHelpers';
import { PageHeader, Card, CardBody, ErrorBanner } from '../components/PageChrome';
import { DataTable } from '../components/DataTable';
import { Pagination } from '../components/Pagination';
import { Modal } from '../components/Modal';
import { FilterSubmitButton } from '../components/FilterSubmitButton';

export default function Feedback() {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [skip, setSkip] = useState(0);
  const [limit] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rating, setRating] = useState('');
  const [viewId, setViewId] = useState(null);
  const [viewData, setViewData] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { skip, limit };
      if (rating) params.rating = Number(rating);
      const raw = await adminApi.listFeedback(params);
      const norm = normalizeListResponse(raw, limit);
      setRows(norm.items);
      setTotalCount(norm.totalCount);
    } catch (e) {
      setError(pickErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [skip, limit, rating]);

  useEffect(() => {
    load();
  }, [load]);

  async function openView(id) {
    setViewId(id);
    setViewData(null);
    setViewLoading(true);
    try {
      const res = await adminApi.getFeedback(id);
      setViewData(res);
    } catch (e) {
      setError(pickErrorMessage(e));
    } finally {
      setViewLoading(false);
    }
  }

  const columns = [
    { key: 'rating', label: 'Rating', render: (r) => r.rating ?? '—' },
    {
      key: 'message',
      label: 'Message',
      render: (r) => (
        <span className="text-truncate d-inline-block" style={{ maxWidth: 280 }} title={r.message || r.comment}>
          {r.message || r.comment || '—'}
        </span>
      ),
    },
    { key: 'userId', label: 'User' },
    {
      key: 'created',
      label: 'When',
      render: (r) => formatDate(r.createdAt || r.created_at),
    },
    {
      key: 'actions',
      label: '',
      width: '100px',
      render: (r) => {
        const id = r.feedbackId || r.id || r._id;
        return (
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openView(id)}>
            View
          </button>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader title="Feedback" subtitle="In-app feedback" />
      <ErrorBanner message={error} />
      <Card className="mb-3">
        <CardBody>
          <div className="row g-2 align-items-end">
            <div className="col-md-3">
              <label className="form-label small mb-1">Rating</label>
              <select className="form-select" value={rating} onChange={(e) => setRating(e.target.value)}>
                <option value="">All</option>
                {[5, 4, 3, 2, 1].map((n) => (
                  <option key={n} value={String(n)}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small mb-1 text-transparent user-select-none" aria-hidden="true">
                Filter
              </label>
              <FilterSubmitButton onClick={() => setSkip(0)} />
            </div>
          </div>
        </CardBody>
      </Card>
      <Card>
        <CardBody className="p-0">
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" />
            </div>
          ) : (
            <DataTable columns={columns} rows={rows} rowKey={(r, i) => r.feedbackId || r.id || r._id || i} />
          )}
          <Pagination totalCount={totalCount} skip={skip} limit={limit} onChange={({ skip: s }) => setSkip(s)} />
        </CardBody>
      </Card>

      {viewId ? (
        <Modal title="Feedback detail" onClose={() => setViewId(null)} footer={<button type="button" className="btn btn-primary" onClick={() => setViewId(null)}>Close</button>}>
          {viewLoading ? (
            <div className="d-flex justify-content-center py-4">
              <div className="spinner-border text-primary" />
            </div>
          ) : viewData ? (
            <dl className="row mb-0 small">
              <dt className="col-sm-3 text-secondary">Rating</dt>
              <dd className="col-sm-9 fw-semibold">{viewData.rating ?? '—'}</dd>
              <dt className="col-sm-3 text-secondary">Comment</dt>
              <dd className="col-sm-9 text-break">{viewData.comment || viewData.message || '—'}</dd>
              <dt className="col-sm-3 text-secondary">User</dt>
              <dd className="col-sm-9 font-monospace text-break small">{viewData.userId || '—'}</dd>
              <dt className="col-sm-3 text-secondary">Submitted</dt>
              <dd className="col-sm-9">{formatDate(viewData.createdAt || viewData.created_at)}</dd>
            </dl>
          ) : (
            <p className="text-secondary mb-0">No feedback data.</p>
          )}
        </Modal>
      ) : null}
    </>
  );
}
